import { type FormEvent, useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Select, type Column } from "@legal-500/legal500-design-system";
import { buildFilesUrl } from "../../../api/filesUrl";
import {
  POWERLIST_IMAGE_UPLOAD_ENDPOINT,
  getSponsorCompany,
  powerlistSponsors,
  searchSponsorCompanies,
  type ImageRef,
  type PowerlistCompany,
  type PowerlistList,
  type Sponsor,
  type SponsorTier,
} from "../../../api/powerlist.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { ErrorBanner } from "../../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../../components/LoadingState";
import { ImageUploadField } from "../../../components/Profiles/ImageUploadField";
import { useQuery } from "../../../hooks/useApi";
import {
  ConfirmModal,
  FormError,
  errorMessage,
  parseSortOrder,
  usePowerlistAccess,
  useToast,
} from "../shared";
import { PowerlistCompanyPicker } from "../PowerlistCompanyPicker";
import styles from "../Powerlist.module.css";

const EDITION_WIDE = "";

const TIER_LABELS: Record<SponsorTier, string> = {
  partnered_with: "Partnered with",
  headline_partner: "Headline partner",
  associate_partner: "Associate partner",
};

const TIER_OPTIONS = (Object.keys(TIER_LABELS) as SponsorTier[]).map(tier => ({
  value: tier,
  label: TIER_LABELS[tier],
}));

function isSponsorTier(value: string): value is SponsorTier {
  return value in TIER_LABELS;
}

type SponsorFormModalProps = {
  isOpen: boolean;
  editionId: string;
  sponsor: Sponsor | null;
  lists: PowerlistList[];
  onClose: () => void;
  onSaved: (sponsor: Sponsor) => void;
};

function SponsorFormModal({
  isOpen,
  editionId,
  sponsor,
  lists,
  onClose,
  onSaved,
}: SponsorFormModalProps) {
  const [placement, setPlacement] = useState(EDITION_WIDE);
  const [company, setCompany] = useState<PowerlistCompany | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [logo, setLogo] = useState<ImageRef | null>(null);
  const [tier, setTier] = useState<SponsorTier>("partnered_with");
  const [sortOrder, setSortOrder] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const companyTouched = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    companyTouched.current = false;
    setPlacement(sponsor?.listId ?? EDITION_WIDE);
    setCompany(null);
    setCompanyId(sponsor?.companyId ?? null);
    setCompanyQuery("");
    setCompanyError(null);
    setDisplayName(sponsor?.displayName ?? "");
    setLogo(sponsor?.logo ?? null);
    setTier(sponsor?.tier ?? "partnered_with");
    setSortOrder(sponsor ? String(sponsor.sortOrder) : "");
    setError(null);
    setSaving(false);
    setUploading(false);
    const linkedCompanyId = sponsor?.companyId;
    if (!linkedCompanyId) return;
    let cancelled = false;
    getSponsorCompany(linkedCompanyId)
      .then(found => {
        if (cancelled || companyTouched.current) return;
        if (found) {
          setCompany(found);
          setCompanyQuery(found.name);
          return;
        }
        setCompanyId(null);
        setCompanyError("The linked company no longer exists. Pick another or save to unlink it.");
      })
      .catch(loadError => {
        if (!cancelled && !companyTouched.current)
          setError(`Couldn't load the sponsor company: ${errorMessage(loadError)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, sponsor]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const order = parseSortOrder(sortOrder);
    const query = companyQuery.trim();
    if (query !== "" && query !== company?.name.trim()) {
      setCompanyError("Pick a company from the list or clear it");
      return;
    }
    if (!displayName.trim()) {
      setError("Enter a display name");
      return;
    }
    if (order === "invalid") {
      setError("Sort order must be a whole number of 0 or more");
      return;
    }
    setSaving(true);
    setError(null);
    const input = {
      listId: placement === EDITION_WIDE ? null : placement,
      companyId,
      displayName: displayName.trim(),
      logo,
      tier,
      sortOrder: order,
    };
    try {
      onSaved(
        sponsor
          ? await powerlistSponsors.update(sponsor.id, input)
          : await powerlistSponsors.create(editionId, input),
      );
    } catch (saveError) {
      setError(errorMessage(saveError));
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={sponsor ? "Edit sponsor" : "New sponsor"}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <FormError message={error} />
        <Select
          id="powerlist-sponsor-placement"
          label="Sponsors"
          value={placement}
          onChange={event => setPlacement(event.target.value)}
          options={[
            { value: EDITION_WIDE, label: "Whole edition" },
            ...lists.map(list => ({ value: list.id, label: `${list.title} list` })),
          ]}
          fullWidth
        />
        <Select
          id="powerlist-sponsor-tier"
          label="Shown as"
          value={tier}
          onChange={event => {
            if (isSponsorTier(event.target.value)) setTier(event.target.value);
          }}
          options={TIER_OPTIONS}
          helperText="Partnered with and Headline partner sponsors appear in the first row; Associate partners appear together in a second row."
          fullWidth
        />
        <PowerlistCompanyPicker
          label="Company (optional)"
          listboxId="powerlist-sponsor-company"
          placeholder="Search companies by name…"
          search={searchSponsorCompanies}
          value={company}
          query={companyQuery}
          onQueryChange={next => {
            companyTouched.current = true;
            setCompanyQuery(next);
            setCompanyError(null);
            if (next.trim() === "") {
              setCompany(null);
              setCompanyId(null);
            }
          }}
          onChange={next => {
            companyTouched.current = true;
            setCompany(next);
            setCompanyId(next.id);
            setCompanyQuery(next.name);
            setCompanyError(null);
            if (!displayName.trim()) setDisplayName(next.name);
          }}
          error={companyError ?? undefined}
          disabled={saving}
        />
        <p className={styles.muted}>
          Optional. Linking a company lets the public site link the sponsor logo to the firm's
          profile for that page's country, when one is published.
        </p>
        <Input
          id="powerlist-sponsor-name"
          label="Display name"
          value={displayName}
          onChange={event => setDisplayName(event.target.value)}
          fullWidth
        />
        <ImageUploadField
          label="Logo"
          value={logo}
          onChange={setLogo}
          slug={displayName}
          uploadEndpoint={POWERLIST_IMAGE_UPLOAD_ENDPOINT}
          uploadLabel="Upload sponsor logo"
          changeLabel="Change sponsor logo"
          removeLabel="Remove sponsor logo"
          disabled={saving}
          onUploadingChange={setUploading}
        />
        <p className={styles.muted}>
          Upload a logo. If none is uploaded and a company is linked, the firm's profile logo is
          used.
        </p>
        <p className={styles.muted}>
          Square logo, at least 400 × 400 px, on a white or transparent background (PNG). It's shown
          in a circle.
        </p>
        <Input
          id="powerlist-sponsor-order"
          label="Sort order"
          type="number"
          min={0}
          inputMode="numeric"
          value={sortOrder}
          onChange={event => setSortOrder(event.target.value)}
          fullWidth
        />
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={saving || uploading}>
            {saving ? "Saving..." : sponsor ? "Save" : "Add sponsor"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type FormState = { open: false } | { open: true; sponsor: Sponsor | null };

type SponsorsTabProps = { editionId: string; lists: PowerlistList[] };

export function SponsorsTab({ editionId, lists }: SponsorsTabProps) {
  const { canWrite, canDelete } = usePowerlistAccess();
  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => powerlistSponsors.list(editionId),
    [editionId],
  );
  const [form, setForm] = useState<FormState>({ open: false });
  const [removing, setRemoving] = useState<Sponsor | null>(null);
  const { toast, success } = useToast();
  const listTitles = new Map(lists.map(list => [list.id, list.title]));
  const sponsors = [...(data ?? [])].sort(
    (a, b) =>
      Number(a.listId !== null) - Number(b.listId !== null) ||
      (listTitles.get(a.listId ?? "") ?? "").localeCompare(listTitles.get(b.listId ?? "") ?? "") ||
      a.sortOrder - b.sortOrder,
  );

  const columns: Column<Sponsor>[] = [
    {
      header: "Placement",
      accessor: "listId",
      render: (_value: unknown, row: Sponsor) =>
        row.listId ? `${listTitles.get(row.listId) ?? "Unknown"} list` : "Whole edition",
    },
    {
      header: "Shown as",
      accessor: "tier",
      render: (_value: unknown, row: Sponsor) => (
        <span className={styles.tag}>{TIER_LABELS[row.tier]}</span>
      ),
    },
    { header: "Display name", accessor: "displayName" },
    {
      header: "Logo",
      accessor: "logo",
      render: (_value: unknown, row: Sponsor) => {
        const url = buildFilesUrl(row.logo, row.displayName);
        return url ? (
          <img className={styles.thumb} src={url} alt={`${row.displayName} logo`} />
        ) : (
          "—"
        );
      },
    },
    { header: "Order", accessor: "sortOrder" },
    ...(canWrite || canDelete
      ? [
          {
            header: "Actions",
            accessor: "id" as const,
            render: (_value: unknown, row: Sponsor) => (
              <div className={styles.actions}>
                {canWrite && (
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`Edit ${row.displayName}`}
                    onClick={() => setForm({ open: true, sponsor: row })}
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    aria-label={`Remove ${row.displayName}`}
                    onClick={() => setRemoving(row)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <p className={styles.muted}>
          Edition sponsors appear on every list without sponsors of its own; list sponsors replace
          them on their list.
        </p>
        {canWrite && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setForm({ open: true, sponsor: null })}
          >
            New sponsor
          </Button>
        )}
      </div>
      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
      {loading && data === null ? (
        <LoadingState message="Loading sponsors..." />
      ) : (
        <DataTable
          data={sponsors}
          columns={columns}
          rowKey="id"
          emptyMessage="This edition has no sponsors."
        />
      )}
      <SponsorFormModal
        isOpen={form.open}
        editionId={editionId}
        sponsor={form.open ? form.sponsor : null}
        lists={lists}
        onClose={() => setForm({ open: false })}
        onSaved={saved => {
          setForm({ open: false });
          success(`${saved.displayName} saved`);
          void refetch();
        }}
      />
      <ConfirmModal
        isOpen={removing !== null}
        title="Remove sponsor"
        message={`Remove ${removing?.displayName ?? "this sponsor"}?`}
        confirmLabel="Remove"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          await powerlistSponsors.remove(removing.id);
          setRemoving(null);
          success("Sponsor removed");
          await refetch();
        }}
      />
      {toast}
    </div>
  );
}
